import { SearchMenuRender } from "./search-menu-render";
import { SearchMenuHandler } from "./search-menu-handler";
import { SearchMenuForm } from "./search-menu-form";

export class SearchMenuListener {
    private static readonly form = new SearchMenuForm();

    public static bindChange(element: HTMLElement, handler: (event: Event) => void): void {
        element.addEventListener("change", handler);
    }

    public static bindClick(element: HTMLElement, handler: (event: Event) => void): void {
        element.addEventListener("click", handler);
    }

    public static bindMouseDown(element: HTMLElement, handler: (event: MouseEvent) => void): void {
        element.addEventListener("mousedown", handler as EventListener);
    }

    public static bindFocus(element: HTMLElement, handler: (event: FocusEvent) => void): void {
        element.addEventListener("focus", handler as EventListener);
    }

    public static bindInput(
        element: HTMLElement,
        handler: (event: Event) => void,
        options?: AddEventListenerOptions,
    ): void {
        element.addEventListener("input", handler, options);
    }

    public static bindShowMoreToggle(element: HTMLElement, handler: (event: Event) => void): void {
        element.addEventListener("show-more:toggle", handler);
    }

    public static bindSubmit(
        element: HTMLFormElement,
        handler: (event: SubmitEvent) => void,
        options?: AddEventListenerOptions,
    ): void {
        element.addEventListener("submit", handler as EventListener, options);
    }

    public static bindTransitionEnd(element: HTMLElement, handler: (event: TransitionEvent) => void): void {
        element.addEventListener("transitionend", handler as EventListener);
    }

    public static unbindTransitionEnd(element: HTMLElement, handler: (event: TransitionEvent) => void): void {
        element.removeEventListener("transitionend", handler as EventListener);
    }

    public static initializeSearchForms(rootEl?: Document | HTMLElement): void {
        const root = rootEl ?? document;

        for (const productForm of SearchMenuListener.form.getProductForms(root)) {
            SearchMenuListener.setupProductForm(productForm);
        }

        const boardForm = SearchMenuListener.form.getBoardForm(root);
        if (boardForm) {
            SearchMenuListener.setupBoardForm(boardForm);
        }
    }

    public static setupProductForm(form: HTMLFormElement): void {
        if (form.dataset.searchMenuFormBound === "1") return;
        form.dataset.searchMenuFormBound = "1";

        SearchMenuListener.bindSubmit(form, (event) => SearchMenuHandler.handleProductSearchSubmit(event));

        const productInput = SearchMenuListener.form.getSearchFormInput(form);
        if (productInput) {
            if (productInput.dataset.searchMenuMode === "navbar") {
                SearchMenuListener.bindMouseDown(productInput, () => {
                    productInput.dataset.openSearchMenuOnNextFocus = "1";
                });
            }

            SearchMenuListener.bindInput(productInput, (event) => {
                const input = SearchMenuListener.form.getEventInput(event);
                if (!input) return;
                SearchMenuHandler.toggleClearButton(input);
            });

            const searchTrigger = SearchMenuListener.form.getSearchTriggerButton(form);
            if (searchTrigger instanceof HTMLElement && productInput.dataset.searchMenuMode === "navbar") {
                SearchMenuListener.bindClick(searchTrigger, (event) => {
                    event.preventDefault();
                    productInput.dataset.openSearchMenuOnNextFocus = "1";
                    productInput.focus();
                });
            }
        }

        const productClearBtn = SearchMenuListener.form.getSearchFormClearButton(form);
        if (productClearBtn instanceof HTMLElement) {
            SearchMenuRender.renderClearButtonLabel(productClearBtn);
            SearchMenuListener.bindClick(productClearBtn, () => SearchMenuHandler.clearProductSearch(form));
        }
    }

    public static setupBoardForm(form: HTMLFormElement): void {
        if (form.dataset.searchMenuFormBound === "1") return;
        form.dataset.searchMenuFormBound = "1";

        SearchMenuListener.bindSubmit(form, (event) => SearchMenuHandler.handleBoardSearchSubmit(event));

        const boardInput = SearchMenuListener.form.getSearchFormInput(form);
        if (boardInput) {
            SearchMenuListener.bindInput(boardInput, (event) => {
                const input = SearchMenuListener.form.getEventInput(event);
                if (!input) return;
                SearchMenuHandler.toggleClearButton(input);
            });
        }

        const boardClearBtn = SearchMenuListener.form.getSearchFormClearButton(form);
        if (boardClearBtn instanceof HTMLElement) {
            SearchMenuRender.renderClearButtonLabel(boardClearBtn);
            SearchMenuListener.bindClick(boardClearBtn, () => SearchMenuHandler.clearBoardSearch(form));
        }
    }
}
